import { SectionTopbarStyled } from './SectionSideBar.styled';

import React, { useEffect, useRef, useState } from 'react';

import { useRouter } from 'next/router';

import { css } from '@emotion/react';
import styled from '@emotion/styled';
import { useSection } from '@OnArk/contexts/section';
import Icon from '@OnArk/SVGs/Icon';
import { insertDuplicate } from '@OnArk/utils/arrayHelpers';
import posthog from '@OnArk/utils/posthog';
import { relativeSavedLabel, useSaveStatus } from '@OnArk/utils/saveStatus';

import { WALKTHROUGH_ENABLED } from '../../GettingStarted/enabled';
import { replaySectionTypeTour } from '../../GettingStarted/SectionTypeTour';
import { sectionTypeTours } from '../../GettingStarted/tours';
import TestSettings from '../TestSettings';

// Pill that names the section the user is editing. Shows the
// section's own name (e.g. "Welcome to Check-In") with the type as a
// smaller, dimmer secondary label.
const TypePillStyled = styled.div(
  ({ theme }) => css`
    display: inline-flex;
    align-items: center;
    gap: 0.6rem;
    min-width: 0;
    height: 3.4rem;
    max-width: 36rem;
    padding: 0 1rem;

    color: ${theme.colors.blackLight};

    > i {
      color: ${theme.colors.clickable};
      flex-shrink: 0;
    }
    .pill-name {
      overflow: hidden;

      font-size: 1.4rem;
      font-weight: 600;
      letter-spacing: 0.01em;
      white-space: nowrap;
      text-overflow: ellipsis;
    }
    .pill-type {
      flex-shrink: 0;
      padding: 0.2rem 0.6rem;

      color: ${theme.colors.grayDark};
      font-size: 1.1rem;
      font-weight: 500;
      letter-spacing: 0.02em;
      text-transform: uppercase;

      background: ${theme.colors.bgLightGrey || theme.colors.background};
      border-radius: 999px;
    }
    button.tour {
      display: inline-flex;
      align-items: center;
      justify-content: center;
      flex-shrink: 0;
      width: 2rem;
      height: 2rem;
      padding: 0;

      color: ${theme.colors.grayMed};

      background: none;
      border: 0;
      border-radius: 999px;
      cursor: pointer;
      transition:
        background 0.15s ease,
        color 0.15s ease;

      &:hover {
        color: ${theme.colors.clickable};
        background: ${theme.colors.bgLightGrey || theme.colors.background};
      }
    }

    @media (max-width: 767px) {
      .pill-type {
        display: none;
      }
    }
  `
);

// Status badge on the right side: "Draft · 3 changes" vs "Live".
// Tells you the publish state at a glance without scanning to the
// bottom of the sidebar.
const StatusBadgeStyled = styled.span<{ tone: 'draft' | 'live' }>(
  ({ theme, tone }) => css`
    display: inline-flex;
    align-items: center;
    gap: 0.4rem;
    padding: 0.4rem 0.8rem;

    color: ${tone === 'draft' ? theme.colors.warning : theme.colors.success};
    font-size: 1.2rem;
    font-weight: 500;
    letter-spacing: 0.02em;

    background: ${tone === 'draft'
      ? theme.colors.warningLight
      : theme.colors.successLight};
    border-radius: 999px;

    &::before {
      content: '';
      flex-shrink: 0;
      width: 0.6rem;
      height: 0.6rem;

      background: currentColor;
      border-radius: 50%;
    }
  `
);

const SaveStatusStyled = styled.span(
  ({ theme }) => css`
    display: inline-flex;
    align-items: center;
    gap: 0.4rem;

    color: ${theme.colors.grayMed};
    font-size: 1.2rem;
    font-style: italic;
    white-space: nowrap;
  `
);

// Ghost icon overflow trigger — flat, no border, hover-only background.
// Sits inline with the pill instead of looking like a separate
// button-shaped chip.
const OverflowStyled = styled.div(
  ({ theme }) => css`
    position: relative;

    > button.trigger {
      display: inline-flex;
      align-items: center;
      justify-content: center;
      width: 3.2rem;
      height: 3.2rem;
      padding: 0;

      color: ${theme.colors.grayDark};

      background: none;
      border: 0;
      border-radius: 999px;
      cursor: pointer;
      transition:
        background 0.15s ease,
        color 0.15s ease;

      &:hover,
      &[aria-expanded='true'] {
        color: ${theme.colors.clickable};
        background: ${theme.colors.bgLightGrey || theme.colors.background};
      }
    }
    > button.trigger .dots {
      display: inline-flex;
      flex-direction: column;
      align-items: center;
      gap: 0.3rem;

      span {
        display: block;
        width: 0.4rem;
        height: 0.4rem;

        background: currentColor;
        border-radius: 50%;
      }
    }

    > menu {
      position: absolute;
      top: calc(100% + 0.4rem);
      right: 0;
      z-index: 10;

      display: flex;
      flex-direction: column;
      min-width: 18rem;
      margin: 0;
      padding: 0.4rem;

      background: ${theme.colors.white};
      border: 1px solid ${theme.colors.border};
      border-radius: ${theme.radius.md};
      box-shadow: ${theme.shadows.lg};

      button {
        display: flex;
        gap: 0.8rem;
        align-items: center;
        padding: 0.8rem 1rem;

        color: ${theme.colors.blackLight};
        font-size: 1.3rem;
        text-align: left;

        background: none;
        border: 0;
        border-radius: 0.4rem;
        cursor: pointer;

        &:hover {
          background: ${theme.colors.whiteHover};
        }
        &.danger {
          color: ${theme.colors.error || theme.colors.warning};
        }
        i {
          flex-shrink: 0;
        }
      }
    }
  `
);

function sectionTypeLabel(type: string): string {
  if (type === 'Checkin') return 'Check-In';

  return type;
}

function SectionOverflowMenu() {
  const { client, course, currentSection, setSections } = useSection();
  const router = useRouter();
  const [open, setOpen] = useState(false);
  // Delete is in flight (API call + the redirect it triggers). Guards the
  // double-click: the menu re-opens while the redirect is still loading, and a
  // second Delete used to fire against the already-deleted section.
  const [deleting, setDeleting] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) return;
    const onDown = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) {
        setOpen(false);
      }
    };

    window.addEventListener('mousedown', onDown);

    return () => window.removeEventListener('mousedown', onDown);
  }, [open]);

  const handleDelete = async () => {
    setOpen(false);
    if (deleting) return;

    if (!confirm('Are you sure you want to delete this section?')) return;
    setDeleting(true);
    try {
      // deleteOneSection is idempotent — a section already removed (other
      // tab, earlier click) resolves null rather than throwing, so we still
      // drop it from the list and land on the course page.
      await client.deleteSection({ id: currentSection.id });
      setSections(items => items.filter(i => i.id !== currentSection.id));
      posthog?.capture('deleteSection');
      await router.push(`/admin/classes/${course.id}`);
    } finally {
      setDeleting(false);
    }
  };

  const handleDuplicate = async () => {
    setOpen(false);
    const { data } = await client.duplicateSection({ id: currentSection.id });

    setSections(items => insertDuplicate(items, currentSection.id, data));
    posthog?.capture('duplicateSection');
    void router.push(`/admin/classes/${course.id}/${data.id}/${data.type}`);
  };

  return (
    <OverflowStyled ref={ref}>
      <button
        aria-expanded={open}
        aria-label="Section actions"
        className="trigger"
        type="button"
        onClick={() => setOpen(o => !o)}
      >
        <span aria-hidden className="dots">
          <span />
          <span />
          <span />
        </span>
      </button>
      {open && (
        <menu>
          <button type="button" onClick={() => void handleDuplicate()}>
            <Icon name="ACTIONS_Duplicate" size="16" />
            Duplicate section
          </button>
          <button
            className="danger"
            disabled={deleting}
            type="button"
            onClick={() => void handleDelete()}
          >
            <Icon name="ACTIONS_Delete" size="16" />
            Delete section
          </button>
        </menu>
      )}
    </OverflowStyled>
  );
}

function SaveStatus() {
  const { lastSavedAt, state } = useSaveStatus();

  if (state === 'saving') return <SaveStatusStyled>Saving…</SaveStatusStyled>;
  if (state === 'saved' && lastSavedAt) {
    return (
      <SaveStatusStyled>
        Saved {relativeSavedLabel(lastSavedAt)}
      </SaveStatusStyled>
    );
  }

  return null;
}

function SectionTopbar({
  icon,
  title = '',
}: {
  title?: string;
  icon?: IconName;
}) {
  const { changes, currentSection } = useSection();
  const type = currentSection.type;
  const typeLabel = sectionTypeLabel(type);
  const showManagement = type !== 'Checkin' && type !== 'Completion';
  const sectionName = currentSection.name || typeLabel;
  const displayTitle = title || sectionName;

  return (
    <SectionTopbarStyled>
      <div
        css={{
          alignItems: 'center',
          display: 'flex',
          flex: 1,
          flexWrap: 'wrap',
          gap: '0.6rem',
          minWidth: 0,
        }}
      >
        <TypePillStyled>
          {icon && <Icon name={icon} size="18" />}
          <span className="pill-name" title={displayTitle}>
            {displayTitle}
          </span>
          {!title && <span className="pill-type">{typeLabel}</span>}
          {WALKTHROUGH_ENABLED && sectionTypeTours[type] && (
            <button
              aria-label={`Tour the ${typeLabel} section editor`}
              className="tour"
              title={`Tour the ${typeLabel} section editor`}
              type="button"
              onClick={() => replaySectionTypeTour(type)}
            >
              <span
                css={{
                  alignItems: 'center',
                  border: '1px solid currentColor',
                  borderRadius: '50%',
                  display: 'inline-flex',
                  fontSize: '1rem',
                  fontWeight: 600,
                  height: '1.5rem',
                  justifyContent: 'center',
                  lineHeight: 1,
                  width: '1.5rem',
                }}
              >
                ?
              </span>
            </button>
          )}
        </TypePillStyled>

        {showManagement && <SectionOverflowMenu />}
      </div>

      <div
        css={{
          alignItems: 'center',
          display: 'flex',
          flexShrink: 0,
          gap: '1rem',
          justifyContent: 'flex-end',
        }}
      >
        {/* Type-specific Settings live on the right because they're
            required for the section to function (Test needs a type).
            Video's source lives INSIDE its editor card — the setup state
            when empty, "change video" in the card header after. */}
        {type === 'Test' && <TestSettings />}
        <SaveStatus />
        <StatusBadgeStyled tone={changes ? 'draft' : 'live'}>
          {changes ? 'Draft' : 'Live'}
        </StatusBadgeStyled>
      </div>
    </SectionTopbarStyled>
  );
}

export default SectionTopbar;
